import { useToast } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

export interface MaintenanceFee {
  id: string
  property_id: string
  repair_date: string
  cost: number
  description?: string
  created_by: string
  created_at: string
  updated_at: string
  deleted_at?: string
  deleted_by?: string
}

export interface MaintenanceFeeInput {
  property_id: string
  repair_date: string
  cost: number
  description?: string
}

// Following the established pattern from src/lib/supabase.ts
async function fetchMaintenanceFees(
  propertyId?: string
): Promise<MaintenanceFee[]> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const response = await fetch(`${supabaseUrl}/functions/v1/maintenance-fees`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      action: 'fetch',
      property_id: propertyId,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to fetch maintenance fees')
  }

  const result = await response.json()
  return result.data || []
}

async function createMaintenanceFee(
  data: MaintenanceFeeInput
): Promise<MaintenanceFee> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const response = await fetch(`${supabaseUrl}/functions/v1/maintenance-fees`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      action: 'create',
      property_id: data.property_id,
      repair_date: data.repair_date,
      cost: data.cost,
      description: data.description,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    console.log({ errorData })
    throw new Error(errorData.error || 'Failed to create maintenance fee')
  }

  const result = await response.json()
  return result.data
}

async function updateMaintenanceFee(
  id: string,
  data: Partial<MaintenanceFeeInput>
): Promise<MaintenanceFee> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const response = await fetch(`${supabaseUrl}/functions/v1/maintenance-fees`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      action: 'update',
      id,
      repair_date: data.repair_date,
      cost: data.cost,
      description: data.description,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to update maintenance fee')
  }

  const result = await response.json()
  return result.data
}

async function deleteMaintenanceFee(id: string): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const response = await fetch(`${supabaseUrl}/functions/v1/maintenance-fees`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      action: 'delete',
      id,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to delete maintenance fee')
  }
}

export function useMaintenanceFees(propertyId?: string) {
  return useQuery({
    queryKey: ['maintenance_fees', propertyId],
    queryFn: () => fetchMaintenanceFees(propertyId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useCreateMaintenanceFee() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: createMaintenanceFee,
    onSuccess: () => {
      // Invalidate all maintenance fees queries
      queryClient.invalidateQueries({ queryKey: ['maintenance_fees'] })

      toast({
        title: 'Taxa de manutenção criada com sucesso!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao criar taxa de manutenção',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    },
  })
}

export function useUpdateMaintenanceFee() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Partial<MaintenanceFeeInput>
    }) => updateMaintenanceFee(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance_fees'] })

      toast({
        title: 'Taxa de manutenção atualizada com sucesso!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atualizar taxa de manutenção',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    },
  })
}

export function useDeleteMaintenanceFee() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: deleteMaintenanceFee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance_fees'] })

      toast({
        title: 'Taxa de manutenção excluída com sucesso!',
        description: 'Esta ação pode ser desfeita posteriormente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir taxa de manutenção',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    },
  })
}
